import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { apiClient, formatDetail } from "@/lib/api";

const AuthContext = createContext(null);

// Must mirror backend ROLE_MODULES
const ALL_OPS = [
  "pilot","fa","medical","document","surat_pilot","surat_fa","hotel","preflight",
  "notice_of","notice_ofc",
  "info_of","info_ofa","info_ofb","info_ofc","info_oft",
  "jktof_of","jktof_ofc","jktof_cash",
];
const READ_ONLY_ROLES = ["viewer", "jktdo"];

const ROLE_MODULES = {
  administrator: ["*"],
  admin_fog_head: ["*"],
  fog_head: ALL_OPS,
  admin_chief_pilot: ["pilot","document","surat_pilot","notice_of","notice_ofc","info_of","info_ofa","info_ofb","info_ofc","info_oft","jktof_of","jktof_cash"],
  admin_chief_pilot_airbus: ["pilot","document","surat_pilot","notice_of","notice_ofc","info_of","info_ofa","info_ofb","info_ofc","info_oft","jktof_of","jktof_cash"],
  admin_chief_fa: ["fa","document","surat_fa","notice_of","notice_ofc","info_of","info_ofa","info_ofb","info_ofc","info_oft","jktof_ofc","jktof_cash"],
  medical: ["preflight"],
  pilot_management: ALL_OPS,
  fa_management: ALL_OPS,
  flight_operation_support: ALL_OPS,
  document_controller: ["document","hotel","surat_pilot","surat_fa","notice_of","notice_ofc","info_of","info_ofa","info_ofb","info_ofc","info_oft","jktof_of","jktof_ofc","jktof_cash"],
  jktof: ["jktof_of","jktof_ofc","jktof_cash"],
  jktdo: ALL_OPS,
  viewer: ALL_OPS,
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    const token = localStorage.getItem("fodm_token");
    if (!token) { setUser(false); setLoading(false); return; }
    try {
      const { data } = await apiClient.get("/auth/me");
      setUser(data);
    } catch {
      localStorage.removeItem("fodm_token");
      setUser(false);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  const login = async (email, password) => {
    try {
      const { data } = await apiClient.post("/auth/login", { email, password });
      localStorage.setItem("fodm_token", data.access_token);
      setUser(data.user);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = () => { localStorage.removeItem("fodm_token"); setUser(false); };

  const hasModule = (module) => {
    if (!user || user === false) return false;
    // Uang Kas open to all EXCEPT medical (matches backend user_has_module)
    if (module === "jktof_cash" && user.role !== "medical") return true;
    const perms = ROLE_MODULES[user.role] || [];
    return perms.includes("*") || perms.includes(module);
  };
  const hasAnyModule = (modules) => modules.some(m => hasModule(m));

  const isAdmin = () => user && (user.role === "administrator" || user.role === "admin_fog_head");
  const isReadOnly = () => user && READ_ONLY_ROLES.includes(user.role);
  // canWrite = has module access AND is not a read-only role
  const canWrite = (module) => hasModule(module) && !isReadOnly();

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, hasModule, hasAnyModule, canWrite, isAdmin, isReadOnly, refresh: fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
